import { useEffect, useId, useRef, useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import useLocale from "../../hooks/localeHook/useLocale";
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_LABELS,
  LANGUAGE_SHORT_LABELS,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "../../constants/locale/types";
import { COLOR_CONSTANTS } from "../../constants/styles";
import { SITE_COLOR } from "../../constants/styles/site";
import { FONT_SIZES, FONT_WEIGHTS } from "../../constants/styles/typography";
import { SidebarIconButton } from "../SideBar/components/SidebarIconButton";

export type LocaleSelectorVariant = "sidebar" | "header" | "site";

export interface LocaleSelectorProps {
  variant: LocaleSelectorVariant;
  /**
   * header·site variant 에서 좁은 화면(`isMobile`)이면 짧은 라벨(`KO`/`EN`/`JA`/`KM`)을
   * 표시한다. 기본값 `false`.
   */
  isMobile?: boolean;
  /**
   * 외부에서 언어 변경 이벤트를 받고 싶을 때(예: 분석 트래킹). 호출 후 메뉴는 닫힘.
   */
  onLanguageChange?: (next: SupportedLanguage) => void;
}

/**
 * 사용자가 명시적으로 언어를 전환할 수 있는 공통 컴포넌트.
 *
 * - 사이드바와 헤더 두 위치에서 동일한 4-language 메뉴를 노출한다.
 * - 라벨은 자기 언어로 표기 (`한국어/English/日本語/ខ្មែរ`) — 본인이 모르는
 *   언어를 영어로만 표기하면 식별 불가.
 * - 클릭 시 atom + i18next + localStorage 모두 동기화 (`useLocale` 어댑터가 처리).
 */
/** 공개 사이트 머리말 트리거(시안 `.gnb-lang`·`.gnb-lang-m`) — 지구본 + 글자 + 아래 화살표, 좁은 화면은 지구본 + 짧은 코드 */
const SITE_TRIGGER_STYLES = {
  desktop: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "36px",
    padding: "0 10px",
    borderRadius: "6px",
    fontSize: "15px",
    fontWeight: 500,
    letterSpacing: "-.01em",
    color: SITE_COLOR.navText,
    "&:hover": { background: SITE_COLOR.hoverTint },
  },
  mobile: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "36px",
    padding: "0 10px",
    fontSize: "14px",
    fontWeight: 500,
    color: SITE_COLOR.ink2,
  },
} as const;

const SITE_CHEVRON_PATH = "M6 8l4 4 4-4";
const SITE_GLOBE_MERIDIANS_PATH =
  "M1.8 8h12.4M8 1.7c1.8 1.8 2.6 3.9 2.6 6.3S9.8 12.5 8 14.3M8 1.7C6.2 3.5 5.4 5.6 5.4 8s.8 4.5 2.6 6.3";

interface SiteLocaleTriggerProps {
  label: string;
  isMobile: boolean;
  open: boolean;
  menuId: string;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

function SiteLocaleTrigger({ label, isMobile, open, menuId, onOpen }: SiteLocaleTriggerProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-expanded={open}
      aria-haspopup="menu"
      aria-controls={open ? menuId : undefined}
      sx={isMobile ? SITE_TRIGGER_STYLES.mobile : SITE_TRIGGER_STYLES.desktop}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <circle
          cx="8"
          cy="8"
          r="6.3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
        />
        <path
          d={SITE_GLOBE_MERIDIANS_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
        />
      </svg>
      {label}
      {!isMobile && (
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path
            d={SITE_CHEVRON_PATH}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </Box>
  );
}

function resolveCurrent(language: string | null | undefined): SupportedLanguage {
  if (!language) return DEFAULT_LANGUAGE;
  const short = language.split("-")[0].toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(short)
    ? (short as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

export function LocaleSelector({
  variant,
  isMobile = false,
  onLanguageChange,
}: LocaleSelectorProps) {
  const [, setLocale] = useLocale();
  const { i18n } = useTranslation();
  // 사이드바/헤더 트리거는 실제 i18next 가 사용 중인 언어를 표시한다. atom 은
  // §11 정리 전까지 ko/en 만 담으므로 atom 을 직접 사용하면 ja/km 선택 시 ko 가 표시된다.
  // useTranslation 이 i18n.language 변경에 자동 subscribe 하지만 t 를 사용하지 않으면
  // re-render 가 트리거되지 않을 수 있어 명시적으로 languageChanged 이벤트를 구독한다.
  const [currentLng, setCurrentLng] = useState<string | undefined>(
    () => i18n.language
  );
  useEffect(() => {
    const handler = (lng: string) => setCurrentLng(lng);
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, [i18n]);
  const current = resolveCurrent(currentLng);
  const sidebarAnchorRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = useId();

  const handleOpenFromEvent = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenFromRef = () => {
    if (sidebarAnchorRef.current) setAnchorEl(sidebarAnchorRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (next: SupportedLanguage) => {
    setLocale({ locale: next });
    onLanguageChange?.(next);
    handleClose();
  };

  const trigger = (() => {
    if (variant === "site") {
      return (
        <SiteLocaleTrigger
          label={isMobile ? LANGUAGE_SHORT_LABELS[current] : LANGUAGE_LABELS[current]}
          isMobile={isMobile}
          open={open}
          menuId={menuId}
          onOpen={handleOpenFromEvent}
        />
      );
    }
    if (variant === "sidebar") {
      return (
        <Box ref={sidebarAnchorRef}>
          <SidebarIconButton
            opacity={0.5}
            onClick={handleOpenFromRef}
            ariaExpanded={open}
            ariaHasPopup="menu"
            ariaControls={open ? menuId : undefined}
            icon={
              <Typography
                variant="subtitle1"
                sx={{ color: COLOR_CONSTANTS.action.active }}
              >
                {current}
              </Typography>
            }
          />
        </Box>
      );
    }
    return (
      <Typography
        component="button"
        onClick={handleOpenFromEvent}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        sx={{
          cursor: "pointer",
          background: "transparent",
          border: "none",
          color: COLOR_CONSTANTS.text.primary,
          fontSize: FONT_SIZES.md,
          fontWeight: FONT_WEIGHTS.medium,
          padding: 0,
        }}
      >
        {isMobile
          ? LANGUAGE_SHORT_LABELS[current]
          : LANGUAGE_LABELS[current]}
      </Typography>
    );
  })();

  return (
    <>
      {trigger}
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { mt: variant === "header" ? 2.5 : 1 } }}
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem
            key={language}
            selected={language === current}
            onClick={() => handleSelect(language)}
            sx={{
              fontSize: FONT_SIZES.md,
              fontWeight: FONT_WEIGHTS.medium,
              color: COLOR_CONSTANTS.text.primary,
              paddingX: 2,
              paddingY: 1,
              minWidth: 140,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            {LANGUAGE_LABELS[language]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
